import { useCallback, useContext, useRef } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { CheckoutContext } from '@/context/CheckoutContext';
import { getCheckoutTemplate } from '@/templates/TemplateRegistry';
import { markApplePayTabUnusable } from '@/utils/applePayTabFallback';
import { getDeviceType } from '@/utils/device';
import { logInfo } from '@/utils/logger';
import { suppressNextCheckoutTemplateTransition } from '@/utils/checkoutTransitions';
import { trackExpressWalletAvailability, type ExpressWalletName } from '@/utils/conversionTracking';

/**
 * Optional per-wallet identity a caller can attach so this hook also emits the
 * per-wallet availability RUM log (see `trackExpressWalletAvailability`). Only
 * callers that render a SINGLE known wallet pass this; the Stripe combined ECE
 * (`PayButtonByDevice`) reports per-wallet itself and omits it here.
 */
export interface ExpressWalletReport {
  wallet: ExpressWalletName;
  provider: string;
  reason?: string;
  loadTimeMs?: number;
  /** Set when `reason` is a client hang-breaker — the armed budget in ms. */
  timeoutMs?: number;
}

/**
 * Generic bridge between express-wallet readiness and template selection.
 *
 * Express wallets (Apple Pay / Google Pay, both Stripe and Adyen) all
 * surface a `onReady(isAvailable: boolean)` callback that fires once the
 * native sheet has resolved whether the device can actually show it.
 * Wallet components should pipe that flag through the callback returned
 * here in addition to their existing `onReady` prop.
 *
 * When `false` is reported, we ask the active {@link CheckoutTemplate}
 * what to do via {@link CheckoutTemplate.onExpressWalletUnavailable}.
 * Templates that critically depend on a native wallet
 * (PREFERRED_PAYMENT_METHOD today) return a fallback `Template` and we
 * swap to it through the existing `setTemplate` context action — yielding
 * a smooth degrade to the full method picker instead of stranding the
 * user on an empty primary CTA.
 *
 * Together with the template swap, we ALSO re-pick the default payment
 * method via the fallback template's resolver, with the failing wallet
 * EXCLUDED from the candidate list. Without this, the FTD branch's
 * Apple-Pay selection (set by `PreferredPaymentTemplate.resolveDefaultPaymentMethod`)
 * would carry over to DEFAULT and the user would land on the very form
 * that just reported unavailable.
 *
 * Intentionally does NOT call `canMakePayments()`-style probes ahead of
 * render: those are unreliable across browsers and contexts. Source of
 * truth is the wallet SDK's own readiness signal.
 *
 * The fallback fires at most once per mount to avoid template thrash
 * when multiple wallets independently report unavailable.
 */
export const useExpressWalletFallback = () => {
  const context = useContext(CheckoutContext);
  const {
    template,
    paymentMethodsAvailable,
    paymentMethodSelected,
    boot,
    actions: { setTemplate, setPaymentMethodSelected },
  } = context;
  const hasFallenBackRef = useRef(false);
  // Read fresh context inside the (identity-stable) callback without widening
  // its dependency array.
  const contextRef = useRef(context);
  contextRef.current = context;

  return useCallback(
    (isAvailable: boolean, report?: ExpressWalletReport) => {
      // Per-wallet RUM signal (Apple Pay / Google Pay) BEFORE any early return,
      // so a `ready` report is logged too — not just the unavailable-fallback
      // path. Guarded + deduped inside the tracker; can never throw.
      if (report) {
        trackExpressWalletAvailability(contextRef.current, {
          wallet: report.wallet,
          provider: report.provider,
          is_available: isAvailable,
          reason: report.reason,
          load_time_ms: report.loadTimeMs,
          timeout_ms: report.timeoutMs,
        });
      }

      if (isAvailable) return;

      // Inside the fallback tab this report IS the dead end: the tab was
      // opened for the sole purpose of running Apple Pay in a top-level
      // window, so there is no further fallback to reach for. Latch it (no-op
      // anywhere else) so the tab tells the shopper, instead of collapsing
      // the button into blank space.
      if (report?.wallet === 'apple_pay') markApplePayTabUnusable();

      if (hasFallenBackRef.current) return;

      const active = getCheckoutTemplate(template.type);
      const fallback = active.onExpressWalletUnavailable();
      if (!fallback) return;

      hasFallenBackRef.current = true;

      // Exclude the failing wallet (the currently-selected method) from
      // the candidate list so the fallback template doesn't re-pick the
      // same broken method. `paymentMethodsAvailable` is otherwise still
      // the full BE-returned list — the user can still see and pick a
      // working method (including the failed wallet, in which case the
      // fallback hook short-circuits via `hasFallenBackRef`).
      const candidateMethods = paymentMethodSelected
        ? paymentMethodsAvailable.filter((pm) => pm.paymentMethodName !== paymentMethodSelected)
        : paymentMethodsAvailable;
      const nextMethod = getCheckoutTemplate(fallback.type).resolveDefaultPaymentMethod({
        device: getDeviceType(),
        paymentMethods: candidateMethods,
        bootData: boot,
      });

      logInfo('Express wallet unavailable; falling back to safer template', {
        from: template.type,
        to: fallback.type,
        previousMethod: paymentMethodSelected,
        nextMethod,
      });

      if (template.features.deferExpressWalletReveal) {
        suppressNextCheckoutTemplateTransition();
      }

      unstable_batchedUpdates(() => {
        setTemplate(fallback);
        if (nextMethod && nextMethod !== paymentMethodSelected) {
          setPaymentMethodSelected(nextMethod);
        }
      });
    },
    [template, paymentMethodsAvailable, paymentMethodSelected, boot, setTemplate, setPaymentMethodSelected]
  );
};
